import React from 'react';
import { useSelector } from 'react-redux';
import { useApiPortalV1AssessmentsCompletedRetrieveQuery } from '@/areas/assessment-schedule/api';
import { getSelectedAssessmentPeriodId, getSelectedAssessmentPeriod } from '@/common/selectors/auth';
import { PageSection } from '@/common/styles/page';
import { Loader2, Clock } from 'lucide-react';
import { ASSESSMENT_PERIOD_OPEN, ASSESSMENT_PERIOD_IN_PROGRESS, ASSESSMENT_PERIOD_SURVEY } from '@/areas/assessment-schedule/constants';

interface SurveyInProgressWidgetProps {
  teamId: number;
}

export const SurveyInProgressWidget: React.FC<SurveyInProgressWidgetProps> = ({ teamId }) => {
  const assessmentPeriodId = useSelector(getSelectedAssessmentPeriodId);
  const selectedAssessmentPeriod = useSelector(getSelectedAssessmentPeriod);

  const { data: surveyCompletion, isLoading } = useApiPortalV1AssessmentsCompletedRetrieveQuery(
    { assessmentPeriodId: assessmentPeriodId ?? 0 },
    { skip: !assessmentPeriodId }
  );

  // Determine status label
  const now = Date.now();
  let statusLabel = 'Assessment';
  let started = false;

  if (selectedAssessmentPeriod?.status === ASSESSMENT_PERIOD_OPEN) {
    statusLabel = 'Waiting to begin';
    started = false;
  } else if (
    selectedAssessmentPeriod?.status === ASSESSMENT_PERIOD_IN_PROGRESS ||
    selectedAssessmentPeriod?.status === ASSESSMENT_PERIOD_SURVEY
  ) {
    // If endDateTime exists and is in the past, consider it wrapping up
    const closeAt = selectedAssessmentPeriod?.endDateTime ? new Date(selectedAssessmentPeriod.endDateTime).getTime() : null;
    if (typeof closeAt === 'number' && closeAt < now) {
      statusLabel = 'Wrapping up';
    } else {
      statusLabel = 'In progress';
    }
    started = true;
  }

  const teamResponses = surveyCompletion?.total_responses ?? 0;

  return (
    <PageSection padbottom>
      {isLoading ? (
        <div className="card-elevated">
          <div className="flex items-center justify-center py-4">
            <Loader2 className="w-5 h-5 animate-spin text-gray-400 mr-2" />
            <span className="text-gray-600">Loading survey progress...</span>
          </div>
        </div>
      ) : !started ? (
        // When waiting to begin, render only the small centered card (no outer wrapping card)
        <div className="flex items-center justify-center py-8">
          <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-6 text-center w-full max-w-xs">
            <Clock className="w-10 h-10 text-blue-600 mx-auto mb-3" />
            <h3 className="heading-lg mb-1">Your Survey Will Start Soon ...</h3>
            <p className="text-description">We'll notify you when responses start coming in.</p>
          </div>
        </div>
      ) : (
        <div className="card-elevated">
          <div className="space-relaxed">
            <div className="flex items-start gap-3">
              <div className="flex-grow">
                <h3 className="heading-lg mb-2">{statusLabel}</h3>
                <p className="text-sm text-gray-900 font-semibold">{teamResponses} responses collected</p>
              </div>
            </div>

          </div>
        </div>
      )}
    </PageSection>
  );
};

